import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Alert, Button, Input, Spin, Tag, Tooltip, Typography } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { useHistory, useLocation } from 'react-router-dom';

import { errorMessage, RequestError } from '../../MarketExplorer/api';
import { formatElapsed } from '../ComputeService';
import { useNow } from '../Jobs';
import {
  createThread,
  fetchBudget,
  fetchThread,
  isJobFailed,
  isJobLive,
  jobMessage,
  listThreads,
  POLL_MS,
  sendMessage,
} from './api';
import ChatMessage, { StepList } from './ChatMessage';
import Memories from './Memories';
import PreviewDrawer, { RejectTarget, SaveTarget } from './PreviewDrawer';
import RejectModal from './RejectModal';
import SaveModal from './SaveModal';
import ThreadList from './ThreadList';
import {
  Budget,
  Constraints,
  Job,
  ProposalCard as ProposalCardData,
  Step,
  ThreadDetail,
  ThreadSummary,
} from './types';
import './styles.css';

/** The open chat lives in the address, beside the module's other filters. */
export const THREAD_PARAM = 'builder.thread';

const EXAMPLE_PROMPT =
  'I want an interesting scenario with EUR/USD and US / EUR bonds';

/** How many of the running turn's latest steps the indicator shows. */
const RUNNING_STEPS = 4;

const parseId = (value: string | null) => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

export const budgetLine = (budget: Budget) =>
  `LLM spend $${Number(budget.spent_usd || 0).toFixed(2)} / $${Number(
    budget.cap_usd || 0,
  ).toFixed(2)}`;

/** A list the backend may send as a bare string, or not at all. */
const asList = (value: string[] | string | null | undefined): string[] =>
  (Array.isArray(value) ? value : value ? [value] : []).filter(Boolean);

/**
 * The chat's constraints in one line. A new chat starts with no instruments:
 * that is said plainly rather than left as an empty "Using:".
 */
const ConstraintsLine: React.FC<{ constraints: Constraints }> = ({
  constraints,
}) => {
  const instruments = asList(constraints.instruments);
  const boostTerms = asList(constraints.boost_terms);
  const parts = [
    constraints.weeks ? `${constraints.weeks} weeks` : '',
    constraints.years?.length === 2
      ? `${constraints.years[0]}–${constraints.years[1]}`
      : '',
    constraints.news_per_turn
      ? `${constraints.news_per_turn} news per turn`
      : '',
    constraints.character || '',
  ].filter(Boolean);
  return (
    <div className="blueprint-builder__constraints" data-testid="constraints">
      {instruments.length > 0 ? (
        <>
          <Typography.Text type="secondary">Using:</Typography.Text>
          {instruments.map(code => (
            <Tag key={code}>{code}</Tag>
          ))}
        </>
      ) : (
        <Typography.Text type="secondary">
          Instruments: not chosen yet
        </Typography.Text>
      )}
      {parts.length > 0 && (
        <Typography.Text type="secondary">
          {instruments.length ? '' : '· '}
          {parts.join(' · ')}
        </Typography.Text>
      )}
      {boostTerms.length > 0 && (
        <Typography.Text type="secondary">
          · more on {boostTerms.join(', ')}
        </Typography.Text>
      )}
    </div>
  );
};

const RunningIndicator: React.FC<{ job: Job | null; steps: Step[] }> = ({
  job,
  steps,
}) => {
  const now = useNow(1000, true);
  const started = job ? Date.parse(job.created_at) : NaN;
  const elapsed = Number.isFinite(started) ? (now - started) / 1000 : null;
  const recent = steps.slice(-RUNNING_STEPS);
  return (
    <div className="blueprint-builder__running" role="status">
      <div className="blueprint-builder__running-head">
        <Spin size="small" />
        <Typography.Text>
          {job?.status === 'queued'
            ? 'Queued: waiting for a worker'
            : 'Working on it'}
          {elapsed != null && elapsed >= 0
            ? ` · ${formatElapsed(elapsed)}`
            : ''}
        </Typography.Text>
      </div>
      {recent.length > 0 && <StepList steps={recent} />}
    </div>
  );
};

/**
 * The admin chat that proposes blueprints (Scenario builder › Builder).
 *
 * A turn is a job: sending starts it, and the open chat is re-read every two
 * seconds while its `active_job` is live. A turn that ends failed or refused
 * is shown as an error, from the thread's `error` message or, when the job is
 * still on the thread, from the job itself.
 */
const Builder: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const threadId = parseId(
    new URLSearchParams(location.search).get(THREAD_PARAM),
  );

  /** The address with `id` as the open chat (none for null), other filters kept. */
  const threadPath = useCallback(
    (id: number | null) => {
      const next = new URLSearchParams(location.search);
      if (id == null) next.delete(THREAD_PARAM);
      else next.set(THREAD_PARAM, String(id));
      const search = next.toString();
      return `${location.pathname}${search ? `?${search}` : ''}`;
    },
    [location.pathname, location.search],
  );

  const selectThread = useCallback(
    (id: number | null) => history.push(threadPath(id)),
    [history, threadPath],
  );

  /* ---------------------------- thread list ---------------------------- */
  const [threads, setThreads] = useState<ThreadSummary[] | null>(null);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadsError, setThreadsError] = useState('');
  const loadThreads = useCallback(async () => {
    setThreadsLoading(true);
    try {
      const { results } = await listThreads();
      setThreads(results || []);
      setThreadsError('');
    } catch (reason) {
      setThreadsError(errorMessage(reason) || 'The chats could not be loaded.');
    } finally {
      setThreadsLoading(false);
    }
  }, []);

  /* ------------------------------- budget ------------------------------ */
  const [budget, setBudget] = useState<Budget | null>(null);
  const [budgetError, setBudgetError] = useState(false);
  const loadBudget = useCallback(async () => {
    try {
      setBudget(await fetchBudget());
      setBudgetError(false);
    } catch (_reason) {
      setBudgetError(true);
    }
  }, []);

  useEffect(() => {
    void loadThreads();
    void loadBudget();
  }, [loadThreads, loadBudget]);

  /* ------------------------------ memories ----------------------------- */
  /** Bumped when a turn ends: its `remember` or `forget` steps change them. */
  const [memoriesKey, setMemoriesKey] = useState(0);

  /* ---------------------------- open thread ---------------------------- */
  const [thread, setThread] = useState<ThreadDetail | null>(null);
  /** A chat this screen just created is already in hand: not re-read. */
  const justCreated = useRef<number | null>(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  /** The turn this screen started or last saw on the thread. */
  const [watchedJob, setWatchedJob] = useState<Job | null>(null);
  const wasLive = useRef(false);
  const [pollTick, setPollTick] = useState(0);
  const [pollError, setPollError] = useState('');

  const onTurnEnded = useCallback(() => {
    void loadBudget();
    void loadThreads();
    setMemoriesKey(key => key + 1);
  }, [loadBudget, loadThreads]);

  const applyThread = useCallback(
    (next: ThreadDetail) => {
      setThread(next);
      const job = next.active_job;
      const live = isJobLive(job);
      setWatchedJob(previous =>
        job ? job : previous && isJobLive(previous) ? null : previous,
      );
      if (wasLive.current && !live) onTurnEnded();
      wasLive.current = live;
    },
    [onTurnEnded],
  );

  useEffect(() => {
    setPollError('');
    if (threadId == null) {
      setThread(null);
      setWatchedJob(null);
      setThreadError('');
      wasLive.current = false;
      return undefined;
    }
    if (justCreated.current === threadId) {
      justCreated.current = null;
      return undefined;
    }
    setThread(null);
    setWatchedJob(null);
    wasLive.current = false;
    const controller = new AbortController();
    setThreadLoading(true);
    setThreadError('');
    fetchThread(threadId, controller.signal)
      .then(value => {
        if (!controller.signal.aborted) applyThread(value);
      })
      .catch(reason => {
        if (!controller.signal.aborted)
          setThreadError(
            errorMessage(reason) || 'This chat could not be loaded.',
          );
      })
      .finally(() => {
        if (!controller.signal.aborted) setThreadLoading(false);
      });
    return () => controller.abort();
  }, [threadId, reloadKey, applyThread]);

  const live = isJobLive(thread?.active_job) || isJobLive(watchedJob);

  // Re-read the open chat every POLL_MS while a turn is live.
  useEffect(() => {
    if (!live || threadId == null || thread?.id !== threadId) return undefined;
    let stopped = false;
    const timer = window.setTimeout(async () => {
      if (typeof document !== 'undefined' && document.hidden) {
        setPollTick(tick => tick + 1);
        return;
      }
      try {
        const next = await fetchThread(threadId);
        if (stopped) return;
        setPollError('');
        applyThread(next);
      } catch (reason) {
        if (stopped) return;
        setPollError(errorMessage(reason) || 'Lost contact with the builder.');
        setPollTick(tick => tick + 1);
      }
    }, POLL_MS);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [live, threadId, thread, pollTick, applyThread]);

  const refreshThread = useCallback(async () => {
    if (threadId == null) return;
    try {
      applyThread(await fetchThread(threadId));
    } catch (_reason) {
      // The next action or poll reads it again.
    }
  }, [threadId, applyThread]);

  /* ------------------------------ composer ----------------------------- */
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const composer = useRef<TextAreaRef>(null);
  const running = live || sending;

  useEffect(() => {
    setSendError('');
  }, [threadId]);

  /**
   * The one way anything is sent: the composer's draft (cleared once sent)
   * and a quick reply to a clarifying question (which leaves the draft be).
   */
  const submit = async (raw: string, fromDraft: boolean) => {
    const text = raw.trim();
    if (!text || running) return;
    setSending(true);
    setSendError('');
    try {
      if (threadId == null) {
        const { thread: created, job } = await createThread(text);
        const turn = job || created.active_job;
        justCreated.current = created.id;
        setThread(created);
        setWatchedJob(turn);
        wasLive.current = isJobLive(turn);
        if (fromDraft) setDraft('');
        selectThread(created.id);
        if (!isJobLive(turn)) onTurnEnded();
        else void loadThreads();
      } else {
        const { message, job } = await sendMessage(threadId, text);
        setThread(previous =>
          previous && previous.id === threadId
            ? {
                ...previous,
                active_job: job,
                messages: previous.messages.some(item => item.id === message.id)
                  ? previous.messages
                  : [...previous.messages, message],
              }
            : previous,
        );
        setWatchedJob(job);
        wasLive.current = isJobLive(job);
        if (fromDraft) setDraft('');
        if (!isJobLive(job)) onTurnEnded();
      }
    } catch (reason) {
      setSendError(errorMessage(reason) || 'The message could not be sent.');
      if (reason instanceof RequestError && reason.status === 409) {
        void refreshThread();
      }
    } finally {
      setSending(false);
    }
  };

  const send = () => submit(draft, true);
  const reply = (text: string) => void submit(text, false);

  /* ------------------------ proposals and dialogs ---------------------- */
  const [previewVersion, setPreviewVersion] = useState<number | null>(null);
  const [previewRefresh, setPreviewRefresh] = useState(0);
  const [saveTarget, setSaveTarget] = useState<SaveTarget | null>(null);
  const [rejectTarget, setRejectTarget] = useState<RejectTarget | null>(null);

  const cardActions = {
    onOpen: (versionId: number) => setPreviewVersion(versionId),
    onSave: (card: ProposalCardData) =>
      setSaveTarget({
        proposalId: card.id,
        versionId: card.latest_version_id,
        title: card.title,
      }),
    onReject: (card: ProposalCardData) =>
      setRejectTarget({ proposalId: card.id, title: card.title }),
    onAsk: (card: ProposalCardData) => {
      setDraft(`About "${card.title}": `);
      window.setTimeout(() => {
        composer.current?.focus({ cursor: 'end' });
      }, 0);
    },
  };

  const afterDecision = () => {
    setPreviewRefresh(value => value + 1);
    void refreshThread();
  };

  /* ------------------------------- render ------------------------------ */
  const activeJob = isJobLive(thread?.active_job)
    ? thread?.active_job || null
    : isJobLive(watchedJob)
      ? watchedJob
      : null;
  const messages = thread?.messages || [];
  const inProgress = activeJob
    ? messages.find(
        message =>
          message.role === 'assistant' && message.job_id === activeJob.id,
      )
    : undefined;
  const runningSteps = activeJob?.steps?.length
    ? activeJob.steps
    : inProgress?.steps || [];
  const shown = inProgress
    ? messages.filter(message => message !== inProgress)
    : messages;
  const failedJob = [thread?.active_job, watchedJob].find(
    (job): job is Job =>
      Boolean(job) &&
      isJobFailed(job) &&
      !messages.some(
        message => message.role === 'error' && message.job_id === job?.id,
      ),
  );

  const pane = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (pane.current) pane.current.scrollTop = pane.current.scrollHeight;
  }, [shown.length, running]);

  // Every hook above has run: the server's switch (SCENARIO_BUILDER_CHAT_ENABLED)
  // decides whether the chat shows at all.
  if (budget?.enabled === false) {
    return (
      <Alert
        type="info"
        showIcon
        data-testid="builder-off"
        title="The chat builder is turned off in this environment."
        description="Nothing here spends or saves. It is switched on per environment (SCENARIO_BUILDER_CHAT_ENABLED)."
      />
    );
  }

  return (
    <div className="blueprint-builder">
      <aside className="blueprint-builder__side">
        <ThreadList
          threads={threads}
          loading={threadsLoading}
          error={threadsError}
          selectedId={threadId}
          onSelect={id => selectThread(id)}
          onNew={() => {
            selectThread(null);
            window.setTimeout(() => composer.current?.focus(), 0);
          }}
          onRetry={() => void loadThreads()}
        />
        <Memories refreshKey={memoriesKey} threadHref={threadPath} />
      </aside>
      <section className="blueprint-builder__main" aria-label="Chat">
        <header className="blueprint-builder__head">
          <Typography.Title level={4}>
            {threadId == null ? 'New chat' : thread?.title || 'Untitled chat'}
          </Typography.Title>
          {thread?.constraints && (
            <ConstraintsLine constraints={thread.constraints} />
          )}
        </header>
        <div className="blueprint-builder__pane" ref={pane}>
          {threadLoading && !thread ? (
            <div className="blueprint-builder__center">
              <Spin description="Loading this chat" />
            </div>
          ) : threadError ? (
            <Alert
              type="error"
              showIcon
              title={threadError}
              action={
                <Button
                  size="small"
                  onClick={() => setReloadKey(key => key + 1)}
                >
                  Retry
                </Button>
              }
            />
          ) : threadId == null || shown.length === 0 ? (
            <div className="blueprint-builder__empty">
              <Typography.Paragraph>
                Describe the scenario you want. The builder answers with two or
                three blueprint proposals, each with a preview you can open,
                save as a draft or reject.
              </Typography.Paragraph>
              {threadId == null && (
                <Button type="dashed" onClick={() => setDraft(EXAMPLE_PROMPT)}>
                  Try: “{EXAMPLE_PROMPT}”
                </Button>
              )}
            </div>
          ) : (
            shown.map(message => (
              <ChatMessage
                key={message.id}
                message={message}
                proposals={thread?.proposals || []}
                onReply={reply}
                replyDisabled={running}
                {...cardActions}
              />
            ))
          )}
          {failedJob && (
            <div className="blueprint-builder__message blueprint-builder__message--error">
              <Alert
                type={failedJob.status === 'canceled' ? 'warning' : 'error'}
                showIcon
                title={
                  failedJob.status === 'refused'
                    ? 'The builder refused this turn'
                    : 'The builder could not finish this turn'
                }
                description={jobMessage(failedJob)}
              />
            </div>
          )}
          {live && <RunningIndicator job={activeJob} steps={runningSteps} />}
        </div>
        {pollError && live && (
          <Alert
            type="warning"
            showIcon
            title={`Still waiting for the turn; the last read failed (${pollError}). Retrying.`}
          />
        )}
        {sendError && (
          <Alert
            type="error"
            showIcon
            closable
            onClose={() => setSendError('')}
            title={sendError}
          />
        )}
        <div className="blueprint-builder__composer">
          <Input.TextArea
            ref={composer}
            aria-label="Message"
            value={draft}
            disabled={running}
            autoSize={{ minRows: 2, maxRows: 8 }}
            placeholder={
              running
                ? 'Wait for this turn to finish…'
                : threadId == null
                  ? 'Describe the scenario you want…'
                  : 'Ask for changes: “make A six weeks and add more ECB news”'
            }
            onChange={event => setDraft(event.target.value)}
            onKeyDown={event => {
              // Enter sends; Shift+Enter keeps a new line. An IME composition
              // (e.g. accented or CJK input) is never sent mid-word.
              if (
                event.key === 'Enter' &&
                !event.shiftKey &&
                !event.nativeEvent.isComposing
              ) {
                event.preventDefault();
                void send();
              }
            }}
          />
          <Button
            type="primary"
            loading={sending}
            disabled={running || !draft.trim()}
            onClick={() => void send()}
          >
            Send
          </Button>
        </div>
        <div className="blueprint-builder__foot">
          {budget ? (
            <Tooltip
              title={`${budget.calls} calls${budget.model ? ` · ${budget.model}` : ''}`}
            >
              <Typography.Text type="secondary" data-testid="budget">
                {budgetLine(budget)}
              </Typography.Text>
            </Tooltip>
          ) : budgetError ? (
            <Typography.Text type="secondary" data-testid="budget">
              LLM spend unavailable
            </Typography.Text>
          ) : null}
          <Typography.Text type="secondary">
            Enter sends · Shift + Enter for a new line
          </Typography.Text>
        </div>
      </section>
      <PreviewDrawer
        versionId={previewVersion}
        refreshKey={previewRefresh}
        onClose={() => setPreviewVersion(null)}
        onSave={setSaveTarget}
        onReject={setRejectTarget}
      />
      <SaveModal
        target={saveTarget}
        onClose={() => setSaveTarget(null)}
        onSettled={afterDecision}
      />
      <RejectModal
        target={rejectTarget}
        onClose={() => setRejectTarget(null)}
        onRejected={() => {
          setRejectTarget(null);
          afterDecision();
        }}
      />
    </div>
  );
};

export default Builder;
